import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  createOhtMessage,
  getOhtClinics,
  getOhtMessage,
  getOhtMessages,
  type CreateOhtMessageInput,
  type OhtClinic,
  type OhtMessage,
  type OhtMessageDetail,
  type OhtMessageRecipient,
  type OhtMessageTargetScope,
  type OhtMessageType,
} from "../../../../api/ohtAdmin";
import { Modal } from "../components/Modal";
import { SortCaret, useSortedRows } from "../components/useSortedRows";
import { PageOutline } from "../components/PageOutline";
import "../Overview/OverviewPage.css";
import "../Clinics/ClinicsPage.css";
import "../Profile/ProfilePage.css";
import "../components/Modal.css";
import "../components/Skeleton.css";
import "./MessagesPage.css";

type SortKey =
  | "sentAt"
  | "messageType"
  | "title"
  | "targetScope"
  | "recipientCount"
  | "acknowledgedCount"
  | "responseCount"
  | "expiresAt";

type MessageStatusFilter = "all" | "requiresAck" | "hasResponses" | "active" | "expired";

function typeLabel(type: OhtMessageType | OhtMessage["type"]): string {
  switch (type) {
    case "Announcement":
    case "announcement":
      return "Announcement";
    case "PolicyUpdate":
    case "policy_update":
      return "Policy update";
    case "CapacityRequest":
    case "capacity_request":
      return "Capacity request";
    case "OperationalNotice":
    case "operational_notice":
      return "Operational notice";
    default:
      return "Message";
  }
}

function typeClass(type: OhtMessageType | OhtMessage["type"]): string {
  switch (type) {
    case "Announcement":
    case "announcement":
      return "announcement";
    case "PolicyUpdate":
    case "policy_update":
      return "policy_update";
    case "CapacityRequest":
    case "capacity_request":
      return "capacity_request";
    case "OperationalNotice":
    case "operational_notice":
    default:
      return "operational_notice";
  }
}

function scopeLabel(scope: OhtMessageTargetScope | OhtMessage["scope"]): string {
  switch (scope) {
    case "AllClinicsInOHT":
    case "all":
      return "All clinics in OHT";
    case "AcceptingClinicsOnly":
    case "accepting":
      return "Accepting clinics only";
    case "SelectedClinics":
    case "selected":
      return "Selected clinics";
    default:
      return "Selected clinics";
  }
}

function fmtNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString();
}

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function isExpired(value: string | null | undefined): boolean {
  if (!value) return false;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

function localDateTimeToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function clinicLabel(clinic: OhtClinic): string {
  const city = clinic.cityName ? ` · ${clinic.cityName}` : "";
  return `${clinic.clinicName}${city}`;
}

function acknowledgementPercent(message: OhtMessage): number | null {
  if (!message.requiresAcknowledgement || message.recipientCount <= 0) return null;
  return Math.round((message.acknowledgedCount / message.recipientCount) * 100);
}

function truncate(text: string, max = 140): string {
  const clean = (text || "").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1)}…`;
}

function StatusPill({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: "good" | "warn" | "danger" | "neutral";
}) {
  const palette: Record<NonNullable<React.ComponentProps<typeof StatusPill>["tone"]>, React.CSSProperties> = {
    good: { background: "#dcfce7", color: "#065f46" },
    warn: { background: "#fef3c7", color: "#92400e" },
    danger: { background: "#fee2e2", color: "#7f1d1d" },
    neutral: { background: "#f1f5f9", color: "#475569" },
  };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        padding: "3px 9px",
        fontSize: 12,
        fontWeight: 800,
        whiteSpace: "nowrap",
        ...palette[tone],
      }}
    >
      {label}
    </span>
  );
}

export default function MessagesPage() {
  const [messageType, setMessageType] = useState<OhtMessageType>("Announcement");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [targetScope, setTargetScope] = useState<OhtMessageTargetScope>("AllClinicsInOHT");
  const [selectedClinicIds, setSelectedClinicIds] = useState<number[]>([]);
  const [requiresAcknowledgement, setRequiresAcknowledgement] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");

  const [rows, setRows] = useState<OhtMessage[]>([]);
  const [clinics, setClinics] = useState<OhtClinic[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | OhtMessageType>("all");
  const [statusFilter, setStatusFilter] = useState<MessageStatusFilter>("all");

  const [selectedMessageId, setSelectedMessageId] = useState<number | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<OhtMessageDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const reloadMessages = useCallback(async (signal?: AbortSignal) => {
    const result = await getOhtMessages(signal);
    setRows(result.items || []);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const [messagesResult, clinicsResult] = await Promise.all([
          getOhtMessages(controller.signal),
          getOhtClinics(controller.signal),
        ]);

        if (cancelled) return;
        setRows(messagesResult.items || []);
        setClinics(clinicsResult.items || []);
      } catch (err: any) {
        if (cancelled || err?.name === "AbortError") return;
        setError(String(err?.message || "Failed to load OHT messages."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (targetScope !== "SelectedClinics") {
      setSelectedClinicIds([]);
    }
  }, [targetScope]);

  useEffect(() => {
    if (selectedMessageId == null) {
      setSelectedDetail(null);
      setDetailError(null);
      return;
    }

    const messageId = selectedMessageId;
    const controller = new AbortController();
    let cancelled = false;

    async function loadDetail() {
      setDetailLoading(true);
      setDetailError(null);

      try {
        const detail = await getOhtMessage(messageId, controller.signal);
        if (!cancelled) setSelectedDetail(detail);
      } catch (err: any) {
        if (cancelled || err?.name === "AbortError") return;
        setDetailError(String(err?.message || "Failed to load message details."));
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    }

    loadDetail();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [selectedMessageId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return rows.filter((message) => {
      if (typeFilter !== "all" && message.messageType !== typeFilter) return false;
      if (statusFilter === "requiresAck" && !message.requiresAcknowledgement) return false;
      if (statusFilter === "hasResponses" && message.responseCount <= 0) return false;
      if (statusFilter === "active" && (!message.isActive || isExpired(message.expiresAt))) return false;
      if (statusFilter === "expired" && !isExpired(message.expiresAt)) return false;

      if (!q) return true;

      return (
        message.title.toLowerCase().includes(q) ||
        message.body.toLowerCase().includes(q) ||
        typeLabel(message.messageType).toLowerCase().includes(q) ||
        scopeLabel(message.targetScope).toLowerCase().includes(q)
      );
    });
  }, [rows, search, typeFilter, statusFilter]);

  const getSortValue = useCallback(
    (message: OhtMessage, key: SortKey): string | number | null => {
      switch (key) {
        case "sentAt":
          return message.sentAt ? new Date(message.sentAt).getTime() : null;
        case "messageType":
          return typeLabel(message.messageType);
        case "title":
          return message.title;
        case "targetScope":
          return scopeLabel(message.targetScope);
        case "recipientCount":
          return message.recipientCount;
        case "acknowledgedCount":
          return message.acknowledgedCount;
        case "responseCount":
          return message.responseCount;
        case "expiresAt":
          return message.expiresAt ? new Date(message.expiresAt).getTime() : null;
      }
    },
    []
  );

  const { sorted, sort, toggleSort } = useSortedRows<OhtMessage, SortKey>(
    filtered,
    getSortValue,
    { key: "sentAt", direction: "desc" }
  );

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, message) => {
        acc.total += 1;
        acc.recipients += message.recipientCount || 0;
        acc.acknowledged += message.acknowledgedCount || 0;
        acc.responses += message.responseCount || 0;
        if (message.requiresAcknowledgement) acc.requiresAck += 1;
        if (isExpired(message.expiresAt)) acc.expired += 1;
        return acc;
      },
      {
        total: 0,
        recipients: 0,
        acknowledged: 0,
        responses: 0,
        requiresAck: 0,
        expired: 0,
      }
    );
  }, [rows]);

  const canSend =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    (targetScope !== "SelectedClinics" || selectedClinicIds.length > 0) &&
    !sending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!title.trim()) {
      setError("Please enter a message title.");
      return;
    }

    if (!body.trim()) {
      setError("Please enter a message body.");
      return;
    }

    if (targetScope === "SelectedClinics" && selectedClinicIds.length === 0) {
      setError("Please select at least one clinic for a selected-clinics message.");
      return;
    }

    const payload: CreateOhtMessageInput = {
      messageType,
      title: title.trim(),
      body: body.trim(),
      targetScope,
      selectedClinicIds: targetScope === "SelectedClinics" ? selectedClinicIds : undefined,
      requiresAcknowledgement,
      expiresAt: localDateTimeToIso(expiresAt),
    };

    setSending(true);

    try {
      const created = await createOhtMessage(payload);
      setRows((prev) => [created, ...prev.filter((m) => m.ohtMessageId !== created.ohtMessageId)]);
      setTitle("");
      setBody("");
      setTargetScope("AllClinicsInOHT");
      setSelectedClinicIds([]);
      setRequiresAcknowledgement(false);
      setExpiresAt("");
      setSuccess("Message sent to the selected clinic audience.");
      await reloadMessages();
    } catch (err: any) {
      setError(String(err?.message || "Failed to send OHT message."));
    } finally {
      setSending(false);
    }
  }

  function toggleClinic(clinicId: number) {
    setSelectedClinicIds((prev) =>
      prev.includes(clinicId)
        ? prev.filter((id) => id !== clinicId)
        : [...prev, clinicId]
    );
  }

  function selectAcceptingClinics() {
    setSelectedClinicIds(
      clinics
        .filter((clinic) => (clinic.acceptingDoctorCount || 0) > 0)
        .map((clinic) => clinic.clinicId)
    );
  }

  return (
    <div className="oht-page">
      <header className="oht-page-header">
        <div className="oht-page-title-row">
          <div>
            <h1 className="oht-page-title">Messages</h1>
            <p className="oht-page-subtitle">
              Send announcements, policy updates, capacity requests, and operational notices to clinics in your OHT.
            </p>
          </div>
        </div>
      </header>

      <section className="ocf-card oht-section">
        <div className="oht-section-head">
          <h2 className="ocf-section-title">Messaging snapshot</h2>
          <span className="oht-section-hint">Live OHT message history</span>
        </div>

        <div className="oht-kpi-grid">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="ocf-card ocf-kpi">
                <span className="oht-skeleton" style={{ height: 13, width: "55%" }} />
                <span className="oht-skeleton" style={{ height: 28, width: "38%", marginTop: 12 }} />
                <span className="oht-skeleton" style={{ height: 12, width: "75%", marginTop: 12 }} />
              </div>
            ))
          ) : (
            <>
              <KpiTile label="Messages sent" value={fmtNumber(totals.total)} hint="Total OHT messages in history." />
              <KpiTile label="Recipient clinics" value={fmtNumber(totals.recipients)} hint="Total recipient rows across all sent messages." />
              <KpiTile label="Acknowledgements" value={fmtNumber(totals.acknowledged)} hint={`${fmtNumber(totals.requiresAck)} message(s) require acknowledgement.`} />
              <KpiTile label="Responses" value={fmtNumber(totals.responses)} hint={`${fmtNumber(totals.expired)} expired message(s).`} />
            </>
          )}
        </div>
      </section>

      {error ? <div className="oht-page-error">{error}</div> : null}
      {success ? <div className="oht-page-success">{success}</div> : null}

      <section className="ocf-card oht-message-form">
        <div className="oht-section-head">
          <h2 className="ocf-section-title">Compose message</h2>
          <span className="oht-section-hint">Delivered to Clinic Admin portal inboxes</span>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="oht-form-grid">
            <div className="oht-filter">
              <label className="oht-filter-label" htmlFor="msg-type">
                Message type
              </label>
              <select
                id="msg-type"
                className="ocf-select"
                value={messageType}
                onChange={(e) => setMessageType(e.target.value as OhtMessageType)}
              >
                <option value="Announcement">Announcement</option>
                <option value="PolicyUpdate">Policy update</option>
                <option value="CapacityRequest">Capacity request</option>
                <option value="OperationalNotice">Operational notice</option>
              </select>
            </div>

            <div className="oht-filter">
              <label className="oht-filter-label" htmlFor="msg-scope">
                Target scope
              </label>
              <select
                id="msg-scope"
                className="ocf-select"
                value={targetScope}
                onChange={(e) => setTargetScope(e.target.value as OhtMessageTargetScope)}
              >
                <option value="AllClinicsInOHT">All clinics in OHT</option>
                <option value="AcceptingClinicsOnly">Accepting clinics only</option>
                <option value="SelectedClinics">Selected clinics</option>
              </select>
            </div>
          </div>

          {targetScope === "SelectedClinics" ? (
            <div className="ocf-card" style={{ padding: 14 }}>
              <div className="oht-section-head" style={{ marginBottom: 10 }}>
                <div>
                  <h3 className="ocf-section-title" style={{ fontSize: 15 }}>Select recipient clinics</h3>
                  <p className="oht-muted" style={{ margin: 0 }}>
                    Select one or more clinics in this OHT. The backend will enforce OHT scope again when sending.
                  </p>
                </div>
                <div className="oht-page-actions">
                  <button type="button" className="ocf-btn ocf-btn--secondary" onClick={selectAcceptingClinics}>
                    Select accepting clinics
                  </button>
                  <button type="button" className="ocf-btn ocf-btn--secondary" onClick={() => setSelectedClinicIds([])}>
                    Clear
                  </button>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
                {clinics.length === 0 ? (
                  <div className="oht-muted">No clinics returned for this OHT.</div>
                ) : (
                  clinics.map((clinic) => (
                    <label key={clinic.clinicId} className="oht-checkbox" style={{ alignItems: "flex-start", padding: 10, border: "1px solid rgba(15, 80, 135, 0.1)", borderRadius: 12 }}>
                      <input
                        type="checkbox"
                        checked={selectedClinicIds.includes(clinic.clinicId)}
                        onChange={() => toggleClinic(clinic.clinicId)}
                      />
                      <span>
                        <strong>{clinicLabel(clinic)}</strong>
                        <br />
                        <span className="oht-muted">
                          {fmtNumber(clinic.acceptingDoctorCount)} accepting doctor(s) · {fmtNumber(clinic.pipelineCount)} active pipeline
                        </span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
          ) : null}

          <div className="oht-filter oht-filter-full">
            <label className="oht-filter-label" htmlFor="msg-title">
              Title
            </label>
            <input
              id="msg-title"
              className="ocf-select"
              type="text"
              placeholder="Brief subject line"
              value={title}
              maxLength={255}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="oht-filter oht-filter-full">
            <label className="oht-filter-label" htmlFor="msg-body">
              Body
            </label>
            <textarea
              id="msg-body"
              className="ocf-select oht-message-body"
              rows={6}
              placeholder="Message contents..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>

          <div className="oht-form-grid">
            <div className="oht-filter">
              <label className="oht-filter-label" htmlFor="msg-expires">
                Expires at
              </label>
              <input
                id="msg-expires"
                className="ocf-select"
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>

            <div className="oht-filter oht-filter-center">
              <label className="oht-checkbox">
                <input
                  type="checkbox"
                  checked={requiresAcknowledgement}
                  onChange={(e) => setRequiresAcknowledgement(e.target.checked)}
                />
                <span>Requires acknowledgement from each recipient clinic</span>
              </label>
            </div>
          </div>

          <div className="oht-form-actions">
            <button
              type="submit"
              className="ocf-btn ocf-btn--primary"
              disabled={!canSend}
            >
              {sending ? "Sending…" : "Send message"}
            </button>
            <span className="oht-form-note oht-muted">
              {targetScope === "SelectedClinics"
                ? `${selectedClinicIds.length} selected clinic${selectedClinicIds.length === 1 ? "" : "s"}`
                : scopeLabel(targetScope)}
            </span>
          </div>
        </form>
      </section>

      <section className="ocf-card oht-filters">
        <h2 className="ocf-section-title">Message history filters</h2>
        <div className="oht-filters-grid" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
          <div className="oht-filter">
            <label className="oht-filter-label" htmlFor="msg-search">
              Search
            </label>
            <input
              id="msg-search"
              className="ocf-select"
              type="search"
              value={search}
              placeholder="Title, body, type, or scope"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="oht-filter">
            <label className="oht-filter-label" htmlFor="msg-type-filter">
              Type
            </label>
            <select
              id="msg-type-filter"
              className="ocf-select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as "all" | OhtMessageType)}
            >
              <option value="all">All types</option>
              <option value="Announcement">Announcement</option>
              <option value="PolicyUpdate">Policy update</option>
              <option value="CapacityRequest">Capacity request</option>
              <option value="OperationalNotice">Operational notice</option>
            </select>
          </div>

          <div className="oht-filter">
            <label className="oht-filter-label" htmlFor="msg-status-filter">
              Status
            </label>
            <select
              id="msg-status-filter"
              className="ocf-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as MessageStatusFilter)}
            >
              <option value="all">All statuses</option>
              <option value="active">Active only</option>
              <option value="expired">Expired only</option>
              <option value="requiresAck">Requires acknowledgement</option>
              <option value="hasResponses">Has clinic responses</option>
            </select>
          </div>
        </div>
      </section>

      <section className="ocf-card oht-table-card">
        <div className="oht-section-head">
          <h2 className="ocf-section-title">
            Sent messages ({loading ? "…" : sorted.length})
          </h2>
          <span className="oht-section-hint">Click any column header to sort</span>
        </div>

        <div className="oht-table-wrap">
          <table className="oht-table oht-table--messages">
            <colgroup>
              <col style={{ width: "13%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "23%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "7%" }} />
            </colgroup>
            <thead>
              <tr>
                <Th sortKey="sentAt" label="Sent" sort={sort} onSort={toggleSort} />
                <Th sortKey="messageType" label="Type" sort={sort} onSort={toggleSort} />
                <Th sortKey="title" label="Title" sort={sort} onSort={toggleSort} />
                <Th sortKey="targetScope" label="Scope" sort={sort} onSort={toggleSort} />
                <Th sortKey="recipientCount" label="Recipients" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="acknowledgedCount" label="Acknowledged" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="responseCount" label="Responses" sort={sort} onSort={toggleSort} numeric />
                <Th sortKey="expiresAt" label="Expires" sort={sort} onSort={toggleSort} />
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="oht-table-empty">Loading messages…</td>
                </tr>
              ) : sorted.length === 0 ? (
                <tr>
                  <td colSpan={9} className="oht-table-empty">No messages match your filters.</td>
                </tr>
              ) : (
                sorted.map((message) => {
                  const ackPct = acknowledgementPercent(message);
                  return (
                    <tr key={message.ohtMessageId}>
                      <td>{fmtDateTime(message.sentAt || message.createdAt)}</td>
                      <td>
                        <span className={`oht-msg-type oht-msg-type--${typeClass(message.messageType)}`}>
                          {typeLabel(message.messageType)}
                        </span>
                      </td>
                      <td>
                        <button
                          className="oht-table-link"
                          type="button"
                          onClick={() => setSelectedMessageId(message.ohtMessageId)}
                        >
                          {message.title}
                        </button>
                        <div className="oht-muted">{truncate(message.body, 90)}</div>
                      </td>
                      <td>{scopeLabel(message.targetScope)}</td>
                      <td className="oht-table-num">{fmtNumber(message.recipientCount)}</td>
                      <td className="oht-table-num">
                        {message.requiresAcknowledgement ? (
                          <>
                            {fmtNumber(message.acknowledgedCount)} / {fmtNumber(message.recipientCount)}
                            {ackPct != null ? <div className="oht-muted">{ackPct}%</div> : null}
                          </>
                        ) : (
                          <StatusPill label="Not required" />
                        )}
                      </td>
                      <td className="oht-table-num">{fmtNumber(message.responseCount)}</td>
                      <td>
                        {message.expiresAt ? (
                          <>
                            {fmtDateTime(message.expiresAt)}
                            {isExpired(message.expiresAt) ? <div><StatusPill label="Expired" tone="warn" /></div> : null}
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="oht-table-num">
                        <button
                          className="ocf-btn ocf-btn--secondary oht-table-action"
                          type="button"
                          onClick={() => setSelectedMessageId(message.ohtMessageId)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <PageOutline
        whatItShows="This page lets OHT Admin users send updates and requests to clinics in their OHT, review past messages, and see whether clinics have acknowledged or responded."
        audience="For OHT Admin users who need a simple way to communicate with clinics about announcements, policy updates, capacity requests, and operational notices."
        updateTrigger="The page refreshes when it opens. New messages appear in the history after they are sent. Opening a message shows the recipient clinics, acknowledgement status, and any clinic responses."
        dataSources={[
          {
            label: "Message history",
            status: "live",
            detail: "Shows messages already sent by the OHT, including recipients, acknowledgements, responses, and expiry status.",
          },
          {
            label: "Compose message",
            status: "live",
            detail: "Allows an OHT Admin to send a message to all clinics, accepting clinics only, or selected clinics.",
          },
          {
            label: "Recipient clinics",
            status: "live",
            detail: "Shows the clinics available for selected-clinic messaging within the OHT area.",
          },
          {
            label: "Acknowledgements and responses",
            status: "live",
            detail: "Shows which clinics have acknowledged a message and whether they have sent a response.",
          },
        ]}
        notes={[
          "Use this page for clinic-facing operational communication, not patient-specific communication.",
          "Messages can be sent to all clinics, only clinics with accepting doctors, or a selected clinic group.",
          "Requiring acknowledgement is useful when the OHT needs confirmation that clinics have seen an important message.",
          "Avoid including patient names, health details, or patient-specific instructions in OHT messages.",
          "Expired messages remain visible in the history for reference.",
        ]}
      />

      <Modal
        open={selectedMessageId != null}
        title={selectedDetail?.title || "Message details"}
        eyebrow={selectedDetail ? typeLabel(selectedDetail.messageType) : "OHT message"}
        onClose={() => setSelectedMessageId(null)}
        size="lg"
      >
        {detailLoading ? (
          <div className="oht-table-empty">Loading message details…</div>
        ) : detailError ? (
          <div className="oht-page-error">{detailError}</div>
        ) : selectedDetail ? (
          <MessageDetailView detail={selectedDetail} />
        ) : (
          <div className="oht-table-empty">No message selected.</div>
        )}
      </Modal>
    </div>
  );
}

function MessageDetailView({ detail }: { detail: OhtMessageDetail }) {
  const acknowledged = detail.recipients.filter((recipient) => recipient.acknowledged).length;
  const responded = detail.recipients.filter((recipient) => recipient.responseText).length;

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <dl className="oht-drawer-kv">
        <dt>Scope</dt>
        <dd>{scopeLabel(detail.targetScope)}</dd>
        <dt>Sent</dt>
        <dd>{fmtDateTime(detail.sentAt || detail.createdAt)}</dd>
        <dt>Created by</dt>
        <dd>{detail.createdByDisplayName || detail.createdByEmail || "—"}</dd>
        <dt>Requires acknowledgement</dt>
        <dd>{detail.requiresAcknowledgement ? "Yes" : "No"}</dd>
        <dt>Expires</dt>
        <dd>{fmtDateTime(detail.expiresAt)}</dd>
        <dt>Recipients</dt>
        <dd>{fmtNumber(detail.recipientCount)}</dd>
        <dt>Acknowledged</dt>
        <dd>{fmtNumber(acknowledged)} / {fmtNumber(detail.recipientCount)}</dd>
        <dt>Responses</dt>
        <dd>{fmtNumber(responded)}</dd>
      </dl>

      <div className="ocf-card" style={{ padding: 16 }}>
        <h3 className="ocf-section-title" style={{ fontSize: 15, marginBottom: 8 }}>Message body</h3>
        <div style={{ whiteSpace: "pre-wrap", color: "#102a43", lineHeight: 1.55 }}>
          {detail.body || "—"}
        </div>
      </div>

      <div className="ocf-card" style={{ padding: 16 }}>
        <h3 className="ocf-section-title" style={{ fontSize: 15, marginBottom: 10 }}>Recipient clinics</h3>
        <div className="oht-table-wrap">
          <table className="oht-table oht-table--messages">
            <thead>
              <tr>
                <th>Clinic</th>
                <th>City</th>
                <th>FSA</th>
                <th>Acknowledged</th>
                <th>Response</th>
              </tr>
            </thead>
            <tbody>
              {detail.recipients.length === 0 ? (
                <tr>
                  <td colSpan={5} className="oht-table-empty">No recipient rows returned.</td>
                </tr>
              ) : (
                detail.recipients.map((recipient) => (
                  <RecipientRow key={recipient.ohtMessageAcknowledgementId || recipient.clinicId} recipient={recipient} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RecipientRow({ recipient }: { recipient: OhtMessageRecipient }) {
  return (
    <tr>
      <td>
        <strong>{recipient.clinicName}</strong>
        {recipient.acknowledgedByDisplayName || recipient.acknowledgedByEmail ? (
          <div className="oht-muted">
            By {recipient.acknowledgedByDisplayName || recipient.acknowledgedByEmail}
          </div>
        ) : null}
      </td>
      <td>{recipient.cityName || "—"}</td>
      <td className="oht-table-mono">{recipient.normalizedFsa || "—"}</td>
      <td>
        {recipient.acknowledged ? (
          <>
            <StatusPill label="Acknowledged" tone="good" />
            <div className="oht-muted">{fmtDateTime(recipient.acknowledgedAt)}</div>
          </>
        ) : (
          <StatusPill label="Pending" tone="warn" />
        )}
      </td>
      <td>
        {recipient.responseText ? (
          <>
            <div style={{ whiteSpace: "pre-wrap" }}>{recipient.responseText}</div>
            <div className="oht-muted">
              {fmtDateTime(recipient.respondedAt)} · {recipient.respondedByDisplayName || recipient.respondedByEmail || "—"}
            </div>
          </>
        ) : (
          "—"
        )}
      </td>
    </tr>
  );
}

function KpiTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint: string;
}) {
  return (
    <div className="ocf-card ocf-kpi">
      <div className="ocf-kpi-label">{label}</div>
      <div className="ocf-big-number">{value}</div>
      <div className="ocf-muted">{hint}</div>
    </div>
  );
}

function Th({
  sortKey,
  label,
  sort,
  onSort,
  numeric,
}: {
  sortKey: SortKey;
  label: string;
  sort: { key: SortKey | null; direction: "asc" | "desc" };
  onSort: (key: SortKey) => void;
  numeric?: boolean;
}) {
  return (
    <th
      className={`oht-th-sortable ${numeric ? "oht-table-num" : ""}`}
      onClick={() => onSort(sortKey)}
      aria-sort={
        sort.key === sortKey
          ? sort.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <span className="oht-th-label">{label}</span>
      <SortCaret
        activeKey={sort.key}
        columnKey={sortKey}
        direction={sort.direction}
      />
    </th>
  );
}